/** @graphql cc **/
import { graphql } from 'react-relay';
import { useMemo } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useContentTitlesQuery$data,
	useContentTitlesQuery$variables,
} from './__generated__/useContentTitlesQuery.graphql.ts';

const ContentTitlesQuery = graphql`
	query useContentTitlesQuery($ids: [ID!]!) {
		pageContent: content(ids: $ids, type: "page") {
			nodes {
				id
				title
			}
		}
		folderContent: content(ids: $ids, type: "folder") {
			nodes {
				id
				title
			}
		}
		whiteboardContent: content(ids: $ids, type: "whiteboard") {
			nodes {
				id
				title
			}
		}
		databaseContent: content(ids: $ids, type: "database") {
			nodes {
				id
				title
			}
		}
		embedContent: content(ids: $ids, type: "embed") {
			nodes {
				id
				title
			}
		}
	}
`;

export type ContentMeta = {
	title: string;
	type: string;
};

export type UseContentTitlesResult = {
	titlesByContentId: Map<string, string>;
	contentMetaById: Map<string, ContentMeta>;
	isLoading: boolean;
};

/**
 * Fetches content titles, types, and emojis for a list of content IDs in a single batch query.
 * Queries page, folder, whiteboard, database, and embed content types to support all content type parentIds.
 * Returns a Map of contentId -> title and contentId -> ContentMeta. IDs with no result or missing title map to empty string.
 */
export const useContentTitles = (contentIds: string[]): UseContentTitlesResult => {
	const ids = useMemo(() => {
		const unique = [...new Set(contentIds)].filter(Boolean) as string[];
		return unique;
	}, [contentIds]);

	const { data, loading } = useQueryInterop<
		useContentTitlesQuery$data,
		useContentTitlesQuery$variables
	>(ContentTitlesQuery, {
		variables: { ids },
		skip: ids.length === 0,
	});

	const { titlesByContentId, contentMetaById } = useMemo(() => {
		const titlesMap = new Map<string, string>();
		const metaMap = new Map<string, ContentMeta>();

		type NodeList = useContentTitlesQuery$data['pageContent'];
		const typedGroups: Array<{ nodes: NodeList; type: string }> = [
			{ nodes: data?.pageContent ?? null, type: 'page' },
			{ nodes: data?.folderContent ?? null, type: 'folder' },
			{ nodes: data?.whiteboardContent ?? null, type: 'whiteboard' },
			{ nodes: data?.databaseContent ?? null, type: 'database' },
			{ nodes: data?.embedContent ?? null, type: 'embed' },
		];

		for (const { nodes, type } of typedGroups) {
			for (const node of nodes?.nodes ?? []) {
				if (node?.id != null) {
					const title = node.title ?? '';
					titlesMap.set(node.id, title);
					metaMap.set(node.id, { title, type });
				}
			}
		}

		return { titlesByContentId: titlesMap, contentMetaById: metaMap };
	}, [data]);

	const isLoading = ids.length > 0 && loading;

	return { titlesByContentId, contentMetaById, isLoading };
};
